import React from 'react';
import { ChevronsRight } from 'lucide-react';
import { KatalogPikto } from './KatalogPikto';
import type { KatalogSprache } from '../../services/mangelKatalog/typen';
import { ariaBeschriftung, naechsterPlatz, tortenZeilen } from './auswahlAnsicht';
import { MITTE_X, MITTE_Y, R_MITTE, STUECKE, VIEWBOX, stueckMitte, stueckPfad } from './radialGeometrie';
import { katalogTexte } from './texte';

/**
 * Tortenmenü („Standardmangel per Torte“, Paket D): 9 Stücke um einen
 * Mittelknopf, als SVG. Das ganze Stück ist Trefferfläche; Pikto und
 * Beschriftung liegen darüber und nehmen keine Zeiger-Ereignisse an.
 *
 * Maße in Einheiten der viewBox (200): bei 336 px Durchmesser ist eine
 * Einheit 1,68 px — die Schrift mit 7 Einheiten also ~11,8 px, bei der
 * Dialog-Torte (320 px) ~11,2 px.
 *
 * Tastatur: Enter/Leertaste lösen ein Stück aus; Pfeile wandern im bzw. gegen
 * den Uhrzeigersinn über die belegten Stücke, Pos1/Ende springen an den
 * Anfang/das Ende. Leere Stücke sind blass, nicht fokussier- und nicht
 * klickbar — so bleiben die Positionen fest.
 */

export interface RadialStueck {
    id: string;
    /** Bis zu zwei Zeilen, getrennt durch '\n' ('Seiten-\nschutz'). */
    beschriftung: string;
    pikto?: string;
    art: 'eintrag' | 'weiter' | 'leer';
    /**
     * 'weiter': Zahl der folgenden Einträge (Anzeige „(+n)“, Screenreader
     * „weitere n“). 'eintrag': wird nur dem Screenreader-Namen angehängt.
     */
    zusatz?: string;
}

export interface RadialMenuProps {
    /** Name des Menüs für Screenreader (z. B. „Thema wählen“). */
    titel: string;
    /** Genau 9 Stücke (fehlende gelten als leer, überzählige fallen weg). */
    stuecke: RadialStueck[];
    /** Beschriftung des Mittelknopfs, bis zu zwei Zeilen ('\n'). */
    mitteBeschriftung: string;
    onWahl: (id: string) => void;
    onWeiter: () => void;
    onMitte: () => void;
    /** Ergänzung: Screenreader-Name der Mitte (sonst die Beschriftung). */
    mitteAriaLabel?: string;
    /** Ergänzung: Größe/Platz des SVG (z. B. `w-[320px]`). */
    className?: string;
    /** Sprache des Blätter-Stücks („Sonstiges“/„More“); Standard deutsch (28.09.). */
    sprache?: KatalogSprache;
}

/** Schriftgröße der Beschriftung (Einheiten der viewBox). */
const SCHRIFT = 7;
const ZEILE = 8;
/** Kantenlänge des Piktogramms (Einheiten der viewBox). */
const PIKTO = 17;
const ABSTAND = 2.5;
/** Radius, auf dem der Block aus Pikto und Schrift zentriert steht. */
const R_INHALT = 65;
/** Oberkante der Schrift bis zur Grundlinie (bei 7 Einheiten). */
const OBERLAENGE = 5.6;

/** Kein Browser-Fokusrahmen (Rechteck um die Box); den Fokus zeigt der Rand des Stücks. */
const OHNE_UMRISS: React.CSSProperties = { outline: 'none' };
const PIKTO_HUELLE: React.CSSProperties = { lineHeight: 0, fontSize: 0 };

const LEER: RadialStueck = { id: '', beschriftung: '', art: 'leer' };

function neunStuecke(stuecke: readonly RadialStueck[]): RadialStueck[] {
    return Array.from({ length: STUECKE }, (_, i) => {
        const s = stuecke[i];
        return s ? s : { ...LEER, id: `__leer${i}` };
    });
}

function aktivierenPerTaste(e: React.KeyboardEvent<SVGGElement>, los: () => void): void {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        los();
    }
}

export const RadialMenu: React.FC<RadialMenuProps> = ({
    titel,
    stuecke,
    mitteBeschriftung,
    onWahl,
    onWeiter,
    onMitte,
    mitteAriaLabel,
    className,
    sprache = 'de',
}) => {
    const t = katalogTexte(sprache);
    const neun = neunStuecke(stuecke);
    const aktiv = neun.map((s) => s.art !== 'leer');

    const pfeile = (e: React.KeyboardEvent<SVGSVGElement>) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
        const ziel = e.target instanceof Element ? e.target.closest('[data-platz]') : null;
        const roh = ziel?.getAttribute('data-platz');
        const aktuell = roh === null || roh === undefined || roh === 'mitte' ? null : Number(roh);
        const naechster = naechsterPlatz(aktuell, e.key, aktiv, 'ring');
        if (naechster === null) return;
        const el = e.currentTarget.querySelector<SVGGElement>(`[data-platz="${naechster}"]`);
        if (!el) return;
        e.preventDefault();
        el.focus();
    };

    const mitteZeilen = tortenZeilen(mitteBeschriftung, 10);

    return (
        <svg
            viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
            role="menu"
            aria-label={titel}
            className={['block aspect-square max-w-full touch-manipulation select-none', className].filter(Boolean).join(' ')}
            onKeyDown={pfeile}
        >
            {neun.map((s, i) => {
                const pfad = stueckPfad(i);
                if (s.art === 'leer') {
                    return (
                        <path
                            key={`platz${i}`}
                            d={pfad}
                            aria-hidden="true"
                            className="pointer-events-none fill-stone-100 stroke-stone-200 opacity-60"
                            strokeWidth={0.8}
                        />
                    );
                }
                const weiter = s.art === 'weiter';
                const zeilen = weiter
                    ? [t.sonstiges, ...(s.zusatz ? [`(+${s.zusatz})`] : [])]
                    : tortenZeilen(s.beschriftung);
                const name = weiter
                    ? `${t.sonstiges}${s.zusatz ? `, ${t.weitere(s.zusatz)}` : ''}`
                    : `${ariaBeschriftung(s.beschriftung)}${s.zusatz ? `, ${s.zusatz}` : ''}`;
                const mitte = stueckMitte(i, R_INHALT);
                const mitBild = weiter || !!s.pikto;
                const bild = weiter ? 13 : PIKTO;
                const hoehe = (mitBild ? bild + ABSTAND : 0) + zeilen.length * ZEILE;
                const oben = mitte.y - hoehe / 2;
                const textOben = oben + (mitBild ? bild + ABSTAND : 0);
                const los = weiter ? onWeiter : () => onWahl(s.id);
                return (
                    <g
                        key={`platz${i}`}
                        role="menuitem"
                        tabIndex={0}
                        data-platz={i}
                        aria-label={name}
                        onClick={los}
                        onKeyDown={(e) => aktivierenPerTaste(e, los)}
                        style={OHNE_UMRISS}
                        className="group cursor-pointer origin-center motion-safe:transition-transform motion-safe:duration-100 motion-safe:active:scale-[0.97]"
                    >
                        <path
                            d={pfad}
                            strokeWidth={0.8}
                            strokeDasharray={weiter ? '3 2' : undefined}
                            className={
                                weiter
                                    ? 'fill-stone-100 stroke-stone-400 group-hover:fill-stone-200 group-active:fill-stone-200 group-focus-visible:fill-stone-200 group-focus-visible:stroke-stone-800 group-focus-visible:stroke-2'
                                    : 'fill-white stroke-stone-300 group-hover:fill-stone-50 group-active:fill-stone-200 group-focus-visible:fill-stone-200 group-focus-visible:stroke-stone-800 group-focus-visible:stroke-2'
                            }
                        />
                        {weiter ? (
                            <ChevronsRight
                                x={mitte.x - bild / 2}
                                y={oben}
                                width={bild}
                                height={bild}
                                aria-hidden="true"
                                className="pointer-events-none text-stone-500"
                            />
                        ) : s.pikto ? (
                            <foreignObject
                                x={mitte.x - bild / 2}
                                y={oben}
                                width={bild}
                                height={bild}
                                aria-hidden="true"
                                className="pointer-events-none"
                            >
                                {/* Flex-Hülle ohne Zeilenhöhe: sonst setzt der Browser den
                                    inline-flex-Kasten auf eine Textzeile und schiebt ihn nach unten. */}
                                <div className="flex h-full w-full items-center justify-center" style={PIKTO_HUELLE}>
                                    <KatalogPikto schluessel={s.pikto} groesse={bild} />
                                </div>
                            </foreignObject>
                        ) : null}
                        <text
                            x={mitte.x}
                            textAnchor="middle"
                            fontSize={SCHRIFT}
                            aria-hidden="true"
                            className={`pointer-events-none font-semibold ${weiter ? 'fill-stone-600' : 'fill-stone-800'}`}
                        >
                            {zeilen.map((z, j) => (
                                <tspan key={j} x={mitte.x} y={textOben + j * ZEILE + OBERLAENGE}>
                                    {z}
                                </tspan>
                            ))}
                        </text>
                    </g>
                );
            })}
            <g
                role="menuitem"
                tabIndex={0}
                data-platz="mitte"
                aria-label={mitteAriaLabel ?? ariaBeschriftung(mitteBeschriftung)}
                onClick={onMitte}
                onKeyDown={(e) => aktivierenPerTaste(e, onMitte)}
                style={OHNE_UMRISS}
                className="group cursor-pointer origin-center motion-safe:transition-transform motion-safe:duration-100 motion-safe:active:scale-95"
            >
                {/* Fokusring im Spalt zwischen Mittelknopf und Ring. */}
                <circle
                    cx={MITTE_X}
                    cy={MITTE_Y}
                    r={R_MITTE + 2}
                    strokeWidth={1.5}
                    className="fill-none stroke-transparent group-focus-visible:stroke-stone-800"
                />
                <circle
                    cx={MITTE_X}
                    cy={MITTE_Y}
                    r={R_MITTE}
                    className="fill-stone-800 group-hover:fill-stone-700 group-active:fill-stone-600 group-focus-visible:fill-stone-700"
                />
                <text
                    x={MITTE_X}
                    textAnchor="middle"
                    fontSize={SCHRIFT}
                    aria-hidden="true"
                    className="pointer-events-none fill-white font-semibold"
                >
                    {mitteZeilen.map((z, j) => (
                        <tspan key={j} x={MITTE_X} y={MITTE_Y - (mitteZeilen.length * ZEILE) / 2 + j * ZEILE + OBERLAENGE}>
                            {z}
                        </tspan>
                    ))}
                </text>
            </g>
        </svg>
    );
};
